import React from "react";
import type { ColumnsType } from "antd/es/table";
import { ISortObj } from "../../../../Models/applicationModels";
import { IAgentsDetailsResp } from "../../agents.model";
import { AGENTS_FIXED_LEFT_DATA_KEYS, AgentsTblKeys } from "../../agents.constants";
import { formatUTCDateTime } from "../../../../utils/formatUTCDate";
import { COLUMN_CONFIG } from "./agentsTable.constants";
import {
    AGENTS_SORTABLE_KEYS,
    getAgentsColumnSortOrder,
} from "./agentsTable.sort.utils";
import { AgentRowActions } from "./agentRowActions/agentRowActions.index";
import styles from "./agentsTable.module.css";

const ACTION_MIN_WIDTH = 110;

const renderStatus = (isActive: boolean) => (
   <span className={isActive ? styles.active : styles.inactive}>{isActive ? 'Active' : 'Inactive'}</span>
);

const renderText = (value: string | null) => value?.trim() || "-";

interface GetAgentsTableColumnsArgs {
    visibleColumns: string[];
    sortRequest: ISortObj | null;
    onCreateDownline: (userId: number) => void;
}

export const getAgentsTableColumns = ({
    visibleColumns,
    sortRequest,
    onCreateDownline,
}: GetAgentsTableColumnsArgs): ColumnsType<IAgentsDetailsResp> => {
    const actionsColumn: ColumnsType<IAgentsDetailsResp>[number] = {
        key: "actions",
        fixed: "left",
        align: "center",
        title: <></>,
        width: ACTION_MIN_WIDTH,
        render: (_value, record) => (
            <AgentRowActions record={record} onCreateDownline={onCreateDownline} />
        ),
        onCell: (_record, rowIndex) => ({
            className:
                rowIndex !== undefined && rowIndex % 2 !== 0
                    ? styles.oddRowStyle
                    : styles.evenRowStyle,
            style: {
                minWidth: `${ACTION_MIN_WIDTH}px`,
            },
        }),
        onHeaderCell: () => ({
            style: {
                minWidth: `${ACTION_MIN_WIDTH}px`,
                width: "unset",
            },
        }),
    };

    const dataColumns: ColumnsType<IAgentsDetailsResp> = COLUMN_CONFIG.filter(({ title }) =>
        visibleColumns.includes(title)
    ).map(({ key, title, width }) => {
        const isSortable = AGENTS_SORTABLE_KEYS.has(key);

        return {
            title,
            dataIndex: key,
            key,
            width,
            ellipsis: true,
            fixed: AGENTS_FIXED_LEFT_DATA_KEYS.has(key) ? ("left" as const) : undefined,
            sorter: isSortable ? true : undefined,
            sortOrder: isSortable ? getAgentsColumnSortOrder(key, sortRequest) : null,
            onCell: (_record, rowIndex) => ({
                className:
                    rowIndex !== undefined && rowIndex % 2 !== 0
                        ? styles.oddRowStyle
                        : styles.evenRowStyle,
            }),
            render: (value: IAgentsDetailsResp[keyof IAgentsDetailsResp]) => {
                switch (key) {
                    case AgentsTblKeys.IS_ACTIVE:
                        return renderStatus(Boolean(value));
                    case AgentsTblKeys.CREATED_ON:
                    case AgentsTblKeys.LAST_LOGIN_ON:
                        return value ? formatUTCDateTime(String(value), false, true) : "-";
                    default:
                        return renderText(value as string | null);
                }
            },
        };
    });

    return [actionsColumn, ...dataColumns];
};
